import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { Col } from "react-bootstrap";
import { IoCloseSharp } from "react-icons/io5";
import DatePicker from "react-datepicker";

import {
  AppliedFilters,
  Container,
  ContainerButton,
  FilterItem,
  FilterKey,
  FiltersContainer,
  FilterValue,
} from "./styles";
import {
  BlueTitle,
  ButtonGroupEnd,
  GrayTitle,
  LoadingGif,
  LoadingOverlay,
  ModalBody,
  ModalBodyContainer,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Row,
  StyledHR,
  TextError,
  TitleAndButton,
} from "../../../shared/styles/global.styles";
import loadSCGif from "../../../assets/load_SC.gif";
import {
  Table,
  TableContainer,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/Table/styles";
import Button from "../../../components/Button/button";
import { Label } from "../../../components/Label/styles";
import SelectComponent from "../../../components/Select/select";
import { DatePickerInputContainer } from "../../../components/Input/styles";
import { Lotacao } from "../../../shared/interfaces/DadosFinanceirosInterface";
import lotacaoService from "../../../services/LotacaoService";
import RelatorioService from "../../../services/RelatorioService";
import { LoadingContainer } from "../../Remessa/ListarRemessas/styles";
import localPagamentoService from "../../../services/LocalPagamentoService";
import { formatDateToBR } from "../../../utils/dateUtils/dateUtils";
import RelatorioPDF from "./RelatorioPDF";
import { pdf } from "@react-pdf/renderer";
import { formatCurrency } from "../../../utils/formatCurrency/formatCurrency";
import { setDate } from "date-fns";
import { RelatorioInadimplenciaDto } from "../../../shared/dtos/RelatorioInadimplencia.dto";
import FichaFinanceiraService from "../../../services/FichaFinanceiraService";
import { progressBarSwalAlert } from "../../../components/ProgressBarSwalAlert/ProgressBarSwalAlert";
import { Colors } from "../../../shared/colors/colors";
import requestInProgressService from "../../../services/RequestInProgressService";

interface LocalPagamento {
  id: number;
  descricao: string;
}

export interface InadimplenciaData {
  nomeContribuinte: string;
  dataGeracao: Date;
  dataVencimento: Date;
  valorParcela: number;
}

const RelatorioInadimplencia: React.FC = () => {
  const [lotacoes, setLotacoes] = useState<Lotacao[]>([]);
  const [localPagamentos, setLocalPagamentos] = useState<LocalPagamento[]>([]);
  const [selectLotacao, setSelectLotacao] = useState<Lotacao>();
  const [selectLocalPagamento, setSelectLocalPagamento] =
    useState<LocalPagamento>();
  const [acumulaPagamento, setAcumulaPagamento] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([undefined, undefined]);
  const [showModalReport, setShowModalReport] = useState<boolean>(false);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRequest, setIsRequest] = useState<boolean>(false);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>({});
  const [relatorioInadimplencia, setRelatorioInadimplencia] =
    useState<RelatorioInadimplenciaDto[]>();
  const [pendingPrint, setPendingPrint] = useState(false);
  const [PDF, setPDF] = useState<Blob>();

  const clearError = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setInvalidFields((prev) => prev.filter((field) => field !== name));
  };

  const fetchLotacoes = async () => {
    try {
      const response = await lotacaoService.getAllLotacoes();
      setLotacoes(response);
    } catch (error) {
      Swal.fire({
        title: "Erro",
        text: "Erro ao buscar lotações. Por favor, tente novamente.",
        icon: "error",
        confirmButtonText: "Fechar",
        allowOutsideClick: false,
      });
    }
  };

  const fetchLocalPagamentos = async () => {
    try {
      const response = await localPagamentoService.getAllLocaisPagamento();
      // Filter out any items with undefined id and map to ensure correct type
      const validLocalPagamentos = response
        .filter((item: any): item is any => item.id !== undefined)
        .map((item) => ({
          id: item.id as number,
          descricao: item.descricao,
        }));
      setLocalPagamentos(validLocalPagamentos);
    } catch (error) {
      Swal.fire({
        title: "Erro",
        text: "Erro ao buscar locais de pagamento. Por favor, tente novamente.",
        icon: "error",
        confirmButtonText: "Fechar",
        allowOutsideClick: false,
      });
    }
  };

  const formatDate = (date: Date): string => {
    return date.toISOString().split("T")[0];
  };

  const generateReport = async () => {
    setIsRequest(true);
    setIsLoading(true);

    const newInvalidFields: string[] = [];

    if ((!dateRange[0] && !dateRange[1]) || !dateRange[0] || !dateRange[1]) {
      newInvalidFields.push("dataVencimento");
    } else {
      if (dateRange[1] < dateRange[0]) {
        newInvalidFields.push("dataVencimento");
      }
    }

    if (newInvalidFields.length > 0) {
      setInvalidFields(newInvalidFields);

      Swal.fire({
        icon: "error",
        title: "Erro nos campos",
        text: "Por favor, preencha todos os campos obrigatórios corretamente.",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      });

      setIsRequest(false);
      setIsLoading(false);
      return;
    }

    const result = await Swal.fire({
      title: "Confirmar",
      text: "Você tem certeza que deseja gerar o relatório de inadimplência dos contribuintes?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim",
      cancelButtonText: "Cancelar",
      allowOutsideClick: false,
    });

    if (result.isConfirmed) {
      Swal.fire({
        title: "Gerando relatório...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      if (dateRange[0] && dateRange[1]) {
        try {
          // Update applied filters
          const newFilters: Record<string, any> = {};

          if (dateRange[0] && dateRange[1]) {
            newFilters.dataInicial = formatDate(dateRange[0]);
            newFilters.dataFinal = formatDate(dateRange[1]);
          }

          if (selectLotacao) {
            newFilters.lotacao = selectLotacao.descricao;
          }

          if (selectLocalPagamento) {
            newFilters.localPagamento = selectLocalPagamento.descricao;
          }

          if (acumulaPagamento) {
            newFilters.acumulaPagamento = acumulaPagamento ? "Sim" : "Não";
          }

          setAppliedFilters(newFilters);

          await RelatorioService.getInadimplenciaReport(
            dateRange[0],
            dateRange[1],
            selectLotacao?.id?.toString(),
            selectLocalPagamento?.id?.toString(),
            acumulaPagamento ? "true" : "false"
          ).then(async (responseRelatorio) => {
            if (responseRelatorio.success) {
              Swal.fire({
                title: "Processando solicitação",
                html: progressBarSwalAlert({
                  currentProgress: 0,
                  totalProgress: 0,
                  color: Colors.primary,
                  showProgressText: true,
                }),
                showConfirmButton: false,
                allowOutsideClick: false,
                didOpen: () => {
                  Swal.showLoading();
                },
              });

              let stopLoopRelatorio = false;
              let getRequestInProgressRelatorio =
                await requestInProgressService.getRequestInProgressById(
                  responseRelatorio.data.id
                );
              await new Promise((resolve) => setTimeout(resolve, 2000));

              while (!stopLoopRelatorio) {
                getRequestInProgressRelatorio =
                  await requestInProgressService.getRequestInProgressById(
                    responseRelatorio.data.id
                  );

                if (getRequestInProgressRelatorio.finished) {
                  stopLoopRelatorio = true;
                } else {
                  Swal.update({
                    html: progressBarSwalAlert({
                      currentProgress:
                        getRequestInProgressRelatorio.currentProgress,
                      totalProgress:
                        getRequestInProgressRelatorio.totalProgress,
                      color: Colors.primary,
                      showProgressText: true,
                      progressText: "Gerando relatório aguarde...",
                    }),
                  });
                  await new Promise((resolve) => setTimeout(resolve, 2000));
                }
              }
              await requestInProgressService.deleteRequestInProgress(
                responseRelatorio.data.id
              );
              if (!getRequestInProgressRelatorio.error) {
                // Se o status da resposta for 'sucesso', exibe uma mensagem de sucesso
                if (getRequestInProgressRelatorio.responseData) {
                  const response: {
                    data: RelatorioInadimplenciaDto[];
                    fileName: string;
                  } = JSON.parse(getRequestInProgressRelatorio.responseData);
                  setRelatorioInadimplencia(response.data);
                  const getPdfInadimplencia = await fetch(
                    `${process.env.REACT_APP_API_URL}pdfs/${response.fileName}`
                  );
                  if (!getPdfInadimplencia.ok) {
                    throw new Error(
                      `Erro ao buscar o PDF: ${getPdfInadimplencia.statusText}`
                    );
                  }
                  const pdfBlobInadimplencia = await getPdfInadimplencia.blob();
                  setPDF(pdfBlobInadimplencia);

                  await FichaFinanceiraService.deleteFileInPdfs(
                    response.fileName
                  );

                  Swal.fire({
                    icon: "success",
                    title: "Sucesso",
                    text: "Relatório gerado com sucesso!",
                    showCancelButton: true,
                    confirmButtonText: "Imprimir",
                    cancelButtonText: "Fechar",
                    allowOutsideClick: false,
                  }).then((willPrint) => {
                    if (willPrint.isConfirmed) {
                      imprimirRelatorio(pdfBlobInadimplencia);
                    }
                  });
                } else {
                  Swal.fire({
                    icon: "error",
                    title: "Erro ao cadastrar parcelas",
                    text: "Ocorreu um erro ao cadastrar as parcelas, por favor tente novamente",
                    allowOutsideClick: false,
                  });
                }
              } else {
                const response: {
                  data: RelatorioInadimplenciaDto[];
                  fileName: string;
                } = JSON.parse(getRequestInProgressRelatorio.responseData);
                const resultDeleteFile =
                  await FichaFinanceiraService.deleteFileInPdfs(
                    response.fileName || ""
                  );
                if (resultDeleteFile.success) {
                  Swal.fire({
                    title: "Erro",
                    text: getRequestInProgressRelatorio.message,
                    icon: "error",
                    confirmButtonText: "OK",
                    allowOutsideClick: false,
                  });
                }
              }
            } else {
              Swal.fire({
                title: "Erro",
                text: "Ocorreu um erro ao solicitar o cadastro das parcelas. Por favor, tente novamente.",
                icon: "error",
                confirmButtonText: "OK",
                allowOutsideClick: false,
              });
            }
          });
        } catch (error: any) {
          console.error("Erro ao gerar relatório:", error);
          Swal.fire({
            icon: "error",
            title: "Erro ao buscar relatório de inadimplência",
            text: error.message || "Erro ao buscar relatório de inadimplência",
            allowOutsideClick: false,
          });
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Erro",
          text: "Datas inválidas para gerar o relatório.",
          allowOutsideClick: false,
        });
      }
    }
    closeReportModal();
  };

  const handleDateRangeChange = (dates: [Date | null, Date | null]) => {
    // Convertendo valores null para undefined
    const [start, end] = dates;

    if (start) {
      setDateRange([start, dateRange[1]]);
    }
    if (end) {
      setDateRange([dateRange[0], end]);
    }
  };

  const closeReportModal = () => {
    setShowModalReport(false);
    setIsRequest(false);
    setIsLoading(false);
  };

  const clearReport = () => {
    setSelectLotacao(undefined);
    setSelectLocalPagamento(undefined);
    setAcumulaPagamento(false);
    setRelatorioInadimplencia(undefined);
    setDateRange([undefined, undefined]);
    setInvalidFields([]);
    setIsRequest(false);
    setIsLoading(false);
    setShowModalReport(false);
    setAppliedFilters([]);
  };

  const imprimirRelatorio = async (pdf: Blob) => {
    try {
      const url = URL.createObjectURL(pdf);
      const newWindow = window.open(url, "_blank");
      if (newWindow) {
        newWindow.focus();
      }
      // Revoga a URL após um pequeno atraso
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return;
    } catch (error) {
      console.error("Erro ao processar o PDF:", error);
    } finally {
      // Garante que o foco volte para a janela principal
      window.focus();
    }
  };

  // Função para formatar a entrada manual de datas
  const handleDateInputChange = (e: any) => {
    if (!e || !e.target || typeof e.target.value !== "string") return;

    let value = e.target.value;

    // Não fazer nada se o campo estiver vazio
    if (!value) return;

    // Verificar se o valor contém hífen (separando intervalo de datas)
    const parts = value.split("-");

    if (parts.length > 1) {
      // Processar intervalo de datas (início - fim)
      let startDate = parts[0].trim();
      let endDate = parts[1].trim();

      // Formatar a primeira data
      startDate = formatDateInput(startDate);

      // Formatar a segunda data
      endDate = formatDateInput(endDate);

      // Recompor o valor com o formato correto
      e.target.value = `${startDate} - ${endDate}`;
    } else {
      // Processar uma única data
      const formattedDate = formatDateInput(value);

      // Verificar se a primeira data está completa (10 caracteres = dd/mm/aaaa)
      // Se estiver completa e não for uma operação de apagar (verificar se o valor atual é maior que o anterior)
      if (formattedDate.length === 10 && value.length >= formattedDate.length) {
        // Adicionar o hífen automaticamente
        e.target.value = `${formattedDate} - `;
      } else {
        e.target.value = formattedDate;
      }
    }
  };

  // Função auxiliar para formatar uma data individual
  const formatDateInput = (value: string): string => {
    // Remover caracteres não numéricos
    let numbers = value.replace(/\D/g, "");

    // Limitar a 8 dígitos (ddmmyyyy)
    numbers = numbers.substring(0, 8);

    // Aplicar máscara dd/mm/yyyy
    let formattedValue = "";
    if (numbers.length > 0) {
      formattedValue = numbers.substring(0, Math.min(2, numbers.length));

      if (numbers.length > 2) {
        formattedValue +=
          "/" + numbers.substring(2, Math.min(4, numbers.length));
      }

      if (numbers.length > 4) {
        formattedValue +=
          "/" + numbers.substring(4, Math.min(8, numbers.length));
      }
    }

    return formattedValue;
  };

  useEffect(() => {
    fetchLotacoes();
    fetchLocalPagamentos();
  }, []);

  return (
    <>
      {!isLoading ? (
        <Container>
          <TitleAndButton>
            <GrayTitle>Relatório de Inadimplência dos Contribuintes</GrayTitle>
            <ContainerButton>
              {Array.isArray(relatorioInadimplencia) && PDF && (
                <Button
                  schemaButton="out-primary"
                  action={() => imprimirRelatorio(PDF)}
                  disabled={isRequest}
                >
                  Imprimir
                </Button>
              )}
              {relatorioInadimplencia && (
                <Button
                  schemaButton="full-primary"
                  action={() => {
                    clearReport();
                  }}
                >
                  Limpar dados do relatório
                </Button>
              )}
              {!relatorioInadimplencia && (
                <Button
                  schemaButton="full-primary"
                  action={() => setShowModalReport(true)}
                  disabled={isRequest}
                >
                  Gerar Relatório
                </Button>
              )}
            </ContainerButton>
          </TitleAndButton>
          <StyledHR />

          {Object.keys(appliedFilters).length > 0 && (
            <AppliedFilters>
              <p>Dados do relatório:</p>
              <FiltersContainer>
                {Object.keys(appliedFilters).length > 0 ? (
                  <>
                    {/* Date range filter */}
                    {appliedFilters.dataInicial && appliedFilters.dataFinal && (
                      <FilterItem>
                        <FilterKey>Intervalo de datas: </FilterKey>
                        <FilterValue>
                          {formatDateToBR(appliedFilters.dataInicial)} -{" "}
                          {formatDateToBR(appliedFilters.dataFinal)}
                        </FilterValue>
                      </FilterItem>
                    )}

                    {/* Lotação filter */}
                    {appliedFilters.lotacao && (
                      <FilterItem>
                        <FilterKey>Lotação: </FilterKey>
                        <FilterValue>{appliedFilters.lotacao}</FilterValue>
                      </FilterItem>
                    )}

                    {/* Local de Pagamento filter */}
                    {appliedFilters.localPagamento && (
                      <FilterItem>
                        <FilterKey>Local de Pagamento: </FilterKey>
                        <FilterValue>
                          {appliedFilters.localPagamento}
                        </FilterValue>
                      </FilterItem>
                    )}

                    {/* Acumular Pagamentos filter */}
                    {appliedFilters.acumulaPagamento && (
                      <FilterItem>
                        <FilterKey>Acumular Pagamentos: </FilterKey>
                        <FilterValue>
                          {appliedFilters.acumulaPagamento}
                        </FilterValue>
                      </FilterItem>
                    )}
                  </>
                ) : (
                  <p></p>
                )}
              </FiltersContainer>
            </AppliedFilters>
          )}

          <TableContainer>
            {isRequest ? (
              <LoadingContainer>
                <LoadingGif src={loadSCGif} alt="Carregando..." />
              </LoadingContainer>
            ) : (
              <Table
                id="relatorio-inadimplencia-tabela"
                style={{ minHeight: "175px" }}
              >
                <TableHead>
                  <TableRow>
                    <TableHeader>Nome Contribuinte</TableHeader>
                    <TableHeader>Contato</TableHeader>
                    <TableHeader>Data de Geração</TableHeader>
                    <TableHeader>Data de Vencimento</TableHeader>
                    <TableHeader>Valor Parcela</TableHeader>
                  </TableRow>
                </TableHead>
                <tbody>
                  {Array.isArray(relatorioInadimplencia) &&
                  relatorioInadimplencia.length > 0 ? (
                    <>
                      {relatorioInadimplencia.map((item, index) => {
                        // Format dates for display
                        const dataGeracao = item.data_geracao
                          ? new Date(
                              new Date(item.data_geracao).getTime() +
                                new Date().getTimezoneOffset() * 60000
                            ).toLocaleDateString("pt-BR")
                          : "-";
                        const dataVencimento = item.data_vencimento
                          ? new Date(
                              new Date(item.data_vencimento).getTime() +
                                new Date().getTimezoneOffset() * 60000
                            ).toLocaleDateString("pt-BR")
                          : "-";

                        // Format currency value
                        const valorParcela = item.valor_parcela
                          ? formatCurrency(item.valor_parcela.toString())
                          : "-";

                        return (
                          <TableRow key={index}>
                            <TableData>
                              {item.nome_contribuinte || "-"}
                            </TableData>
                            <TableData>{item.contato || "-"}</TableData>
                            <TableData>{dataGeracao}</TableData>
                            <TableData>{dataVencimento}</TableData>
                            <TableData>{valorParcela}</TableData>
                          </TableRow>
                        );
                      })}
                    </>
                  ) : (
                    <TableRow>
                      <TableData colSpan={5} className="text-center text-muted">
                        {"Sem resultados"}
                      </TableData>
                    </TableRow>
                  )}
                </tbody>
              </Table>
            )}
          </TableContainer>

          <ModalContainer show={showModalReport}>
            <ModalContent style={{ marginTop: "-12%" }} width={50}>
              <Button
                type="button"
                schemaButton="x-close"
                action={() => closeReportModal()}
                icon={<IoCloseSharp size={30} />}
                position="absolute"
                top={10}
                right={10}
              />
              <ModalHeader>
                <BlueTitle>Gerar relatório de inadimplência</BlueTitle>
              </ModalHeader>

              <ModalBody>
                <Row>
                  <Col>
                    <Label htmlFor="lotacao">Lotação</Label>
                    <SelectComponent
                      color="black"
                      placeholder="Todas Lotações"
                      isPlaceHolder={false}
                      id="lotacao"
                      width={Number("100%")}
                      name="lotacao"
                      disabled={isRequest}
                      value={selectLotacao?.id?.toString() ?? ""}
                      onChange={(e) => {
                        if (e.target.value === "0") {
                          setSelectLotacao({ id: 0, descricao: "Sem lotação" });
                        } else {
                          const selectedLotacao = lotacoes.find(
                            (lotacao) => lotacao.id === parseInt(e.target.value)
                          );
                          setSelectLotacao(selectedLotacao);
                        }
                        clearError(e);
                      }}
                      options={[
                        { value: "0", label: "Sem lotação" },
                        ...lotacoes.map((lotacao) => ({
                          value: lotacao.id!.toString(),
                          label: lotacao.descricao,
                        })),
                      ]}
                    />
                  </Col>
                  <Col>
                    <Label htmlFor="localPagamento">Local de Pagamento</Label>
                    <SelectComponent
                      color="black"
                      placeholder="Todos locais de pagamento"
                      isPlaceHolder={false}
                      id="localPagamento"
                      width={Number("100%")}
                      name="localPagamento"
                      disabled={isRequest}
                      value={selectLocalPagamento?.id?.toString() ?? ""}
                      onChange={(e) => {
                        const selectedLocalPagamento = localPagamentos.find(
                          (local) => local.id === parseInt(e.target.value)
                        );
                        setSelectLocalPagamento(selectedLocalPagamento);
                        clearError(e);
                      }}
                      options={localPagamentos.map((local) => ({
                        value: local.id.toString(),
                        label: local.descricao,
                      }))}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Label htmlFor="acumulaPagamento">
                      Acumular Pagamentos
                    </Label>
                    <SelectComponent
                      color="black"
                      placeholder="Selecione"
                      isPlaceHolder={false}
                      id="acumulaPagamento"
                      width={Number("100%")}
                      name="acumulaPagamento"
                      disabled={isRequest}
                      value={acumulaPagamento ? "true" : "false"}
                      onChange={(e) => {
                        setAcumulaPagamento(e.target.value === "true");
                        clearError(e);
                      }}
                      options={[
                        { value: "true", label: "Sim" },
                        { value: "false", label: "Não" },
                      ]}
                    />
                  </Col>
                  <Col>
                    <Label className="filtro-label" htmlFor="dataVencimento">
                      Data de vencimento <TextError>*</TextError>
                    </Label>
                    <DatePickerInputContainer
                      width={Number("100%")}
                      hasIconLeft={false}
                      hasIconRight={false}
                      error={
                        invalidFields.includes("dataVencimento") ? true : false
                      }
                      className={
                        dateRange[0] || dateRange[1] ? "has-date-content" : ""
                      }
                    >
                      <DatePicker
                        selectsRange={true}
                        name="dataVencimento"
                        startDate={dateRange[0] as Date | undefined}
                        endDate={dateRange[1] as Date | undefined}
                        onChange={(dates) => {
                          handleDateRangeChange(
                            dates as [Date | null, Date | null]
                          );

                          setInvalidFields((prev) =>
                            prev.filter((field) => field !== "dataVencimento")
                          );
                        }}
                        onChangeRaw={handleDateInputChange}
                        calendarStartDay={0}
                        className={`date-picker-input placeholder-custom ${
                          dateRange[0] || dateRange[1]
                            ? "has-value"
                            : "no-value"
                        }`}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="00/00/0000 - 00/00/0000"
                        showPopperArrow={false}
                        popperPlacement="bottom-start"
                        isClearable={false}
                        showYearDropdown
                        showMonthDropdown
                        scrollableMonthYearDropdown
                        wrapperClassName={
                          dateRange[0] || dateRange[1]
                            ? "date-wrapper-filled"
                            : "date-wrapper-empty"
                        }
                        locale="pt-BR"
                        autoComplete="off"
                        allowSameDay
                        onKeyDown={(e) => {
                          if (e.key === "Delete" || e.key === "Backspace") {
                            setDateRange([undefined, undefined]);
                            return;
                          }
                        }}
                      />
                    </DatePickerInputContainer>
                    {invalidFields.includes("dataVencimento") && (
                      <TextError>
                        Preencha a data de vencimento corretamente
                      </TextError>
                    )}
                  </Col>
                </Row>
              </ModalBody>
              <ModalFooter>
                <Button
                  schemaButton="full-primary"
                  action={() => {
                    generateReport();
                  }}
                  disabled={isRequest}
                >
                  Gerar Relatório
                </Button>
              </ModalFooter>
            </ModalContent>
          </ModalContainer>
        </Container>
      ) : (
        <LoadingOverlay>
          <LoadingGif src={loadSCGif} alt="Carregando..." />
        </LoadingOverlay>
      )}
    </>
  );
};

export default RelatorioInadimplencia;
